import React from "react";
import { useTranslation } from "react-i18next";
import { servicesInfo } from "./data";
import Modal from "../../../parts/modal-new";
import SvgIcon from "../../../parts/SvgIcon";
import ServiceMedia from "./ServiceMedia";

const InfoModal = ({ service }) => {
  const { t } = useTranslation("coaching-portal");
  const data = servicesInfo[service] || null;

  if (!data) return null;

  const dataKey = data.dataKey;
  const title = t(`services.data.${dataKey}.title`);

  const getBriefLines = (content) => {
    if (Array.isArray(content))
      return content.map((line, i) => (
        <p
          key={`brief-${i}-${String(line).slice(0, 20)}`}
          className="p5"
          style={i !== content.length - 1 ? { marginBottom: "0.25rem" } : null}
        >
          {line}
        </p>
      ));
    return <p className="p5">{content}</p>;
  };

  const boldInitLine = (line) => {
    if (!line || !String(line).includes(":")) return line;
    const splitted = String(line).split(":");
    return (
      <>
        <span className={splitted[1] ? "font-semibold" : undefined}>
          {splitted[0]}:
        </span>{" "}
        {splitted[1]}
      </>
    );
  };

  const briefRaw = t(`services.data.${dataKey}.brief`, { returnObjects: true });
  const brief = briefRaw;

  const requirementSublineKey = `services.data.${dataKey}.requirementSubline`;
  const requirementSubline =
    t(requirementSublineKey) !== requirementSublineKey
      ? t(requirementSublineKey)
      : null;

  let requirementsContent = null;
  if (dataKey === "programQuiz") {
    const note = t(`services.data.${dataKey}.requirementsNote`);
    const reqTitle = t(`services.data.${dataKey}.requirementsTitle`);
    const items = t(`services.data.${dataKey}.requirementsItems`, {
      returnObjects: true,
    });
    requirementsContent = (
      <>
        <p className="p5">
          <SvgIcon src={"/img/new-icons/check-circle.svg"} />
          {note}
        </p>
        <p className="p5 font-semibold">{reqTitle}</p>
        <ol className="opacity-70 list-inside ml-8">
          {Array.isArray(items) &&
            items.map((item, i) => (
              <li key={i} className="mb-1 list-disc text-[0.75rem]">
                {item}
              </li>
            ))}
        </ol>
      </>
    );
  } else {
    const requirementsRaw = t(`services.data.${dataKey}.requirements`, {
      returnObjects: true,
    });
    const requirements = Array.isArray(requirementsRaw) ? requirementsRaw : [];
    requirementsContent = requirements.map((req, i) => (
      <p key={i} className="p5">
        <SvgIcon src={"/img/new-icons/check-circle.svg"} />
        {boldInitLine(req)}
      </p>
    ));
  }

  const timelineRaw = t(`services.data.${dataKey}.timeline`, {
    returnObjects: true,
  });
  const timeline = Array.isArray(timelineRaw) ? timelineRaw : [];

  const whatyougetRaw = t(`services.data.${dataKey}.whatyouget`, {
    returnObjects: true,
  });
  const whatyouget = Array.isArray(whatyougetRaw) ? whatyougetRaw : [];

  const chipsRaw =
    data.chips &&
    t(`services.data.${dataKey}.chips`, { returnObjects: true });
  const chips = Array.isArray(chipsRaw) ? chipsRaw : [];

  const footerTextKey = `services.data.${dataKey}.footerText`;
  const footerText =
    data.footerLink && t(footerTextKey) !== footerTextKey
      ? t(footerTextKey)
      : null;

  const packagesTiers =
    data.packagesTiers &&
    t(`services.data.${dataKey}.packages`, { returnObjects: true });
  const packagesListRaw =
    data.packagesList &&
    t(`services.data.${dataKey}.packagesList`, { returnObjects: true });
  const packagesList = Array.isArray(packagesListRaw) ? packagesListRaw : [];

  return (
    <Modal title={title} className="service-modal">
      <div className="body">
        <aside>
          <ServiceMedia src={data.img} alt={t("services.serviceAlt")} />
          <p>{title}</p>
          <p className="opacity-60 text-[0.875rem]">
            ${data.price} USD{" "}
            {dataKey === "appStoreListing" && (
              <span>{t("services.oneTimeAppleNote")}</span>
            )}
          </p>
          <div className="flex gap-2 items-center mt-4">
            <a
              role="button"
              data-button-type="primary"
              href={data.link}
              target="_blank"
              className="grow "
            >
              {t("services.buyNow")}
            </a>
            {data.previewLink && (
              <a
                role="button"
                data-button-type="outline"
                href={data.previewLink}
                target="_blank"
              >
                {t("services.previewExamples")}
              </a>
            )}
          </div>

          {footerText && data.footerLink && (
            <footer>
              <p className="p2">{footerText}</p>
              <a
                role="button"
                data-button-type="outline"
                href={data.footerLink}
              >
                <span>{t("services.requestNow")}</span>
              </a>
            </footer>
          )}
        </aside>
        <section>
          <h4>{t("services.description")}</h4>
          {getBriefLines(brief)}

          {chips.length > 0 &&
            chips.map((chip, i) => (
              <span key={i} className="chip info">
                {chip}
              </span>
            ))}
          {whatyouget.length > 0 && (
            <>
              <h4>{t("services.whatYouGet")}</h4>
              {whatyouget.map((item, i) => (
                <p key={i} className="p5">
                  <SvgIcon
                    src="/img/new-icons/check-circle.svg"
                    noFilterOnLight={true}
                  />
                  {item}
                </p>
              ))}
            </>
          )}
          {packagesTiers &&
            typeof packagesTiers === "object" &&
            (packagesTiers.bronze ||
              packagesTiers.silver ||
              packagesTiers.gold) && (
              <>
                <h4>{t("services.availablePackages")}</h4>
                <div className="flex gap-4 items-center mb-6 bg-(--white-05) rounded-(--border-radius) p-4">
                  {packagesTiers.bronze && (
                    <div className="grow">
                      <h5>{t("services.bronzePackage")}</h5>
                      {packagesTiers.bronze.map((item, i) => (
                        <p key={i} className="p5">
                          <SvgIcon
                            src="/img/new-icons/check-circle.svg"
                            noFilterOnLight={true}
                          />
                          {item}
                        </p>
                      ))}
                    </div>
                  )}
                  {packagesTiers.silver && (
                    <div className="grow">
                      <h5>{t("services.silverPackage")}</h5>
                      {packagesTiers.silver.map((item, i) => (
                        <p key={i} className="p5">
                          <SvgIcon
                            src="/img/new-icons/check-circle.svg"
                            noFilterOnLight={true}
                          />
                          {item}
                        </p>
                      ))}
                    </div>
                  )}
                  {packagesTiers.gold && (
                    <div className="grow">
                      <h5>{t("services.goldPackage")}</h5>
                      {packagesTiers.gold.map((item, i) => (
                        <p key={i} className="p5">
                          <SvgIcon
                            src="/img/new-icons/check-circle.svg"
                            noFilterOnLight={true}
                          />
                          {item}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          {packagesList.length > 0 && (
            <>
              <h4>{t("services.availablePackages")}</h4>
              <div>
                {packagesList.map((item, i) => (
                  <p key={i} className="p5">
                    <SvgIcon
                      src="/img/new-icons/check-circle.svg"
                      noFilterOnLight={true}
                    />
                    {item}
                  </p>
                ))}
              </div>
            </>
          )}
          <h4 className="mt-6">{t("services.requirements")}</h4>
          {requirementSubline && (
            <p className="p5 subline">{requirementSubline}</p>
          )}
          {requirementsContent}
          <h4 className="mt-8">{t("services.timeline")}</h4>
          <ul>
            {timeline.map((step, i) => (
              <li key={i}>
                <span>{i + 1}</span>
                <div>
                  <div>
                    <p>{step.phase}</p>
                    <p className="p5">{step.time}</p>
                  </div>
                  <p>{step.upTo}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  );
};

export default InfoModal;
